import {Entity} from "./entity";
import {Expose, Transform} from "class-transformer";
import {DateTime} from "luxon";
import {ChangeTracker} from "../../change-tracking/change-tracker";
import {Public} from "../../decorators/public.decorator";

/**
 * A User's own, private label vocabulary — a flat list of text labels. Distinct
 * from the shared, tenant-wide `Keyword` entity (see backend `PersonalLabel`
 * doc comment for why they are not the same concept).
 *
 * No `owner` field: the backend sets/reads it exclusively from the
 * authenticated session (never trusts a client-supplied value), and reads are
 * already scoped to the caller's own rows server-side — the frontend never
 * needs to reason about whose row it got back.
 */
export class PersonalLabel extends Entity<PersonalLabel> {

    /**
     * `@Public()` (Anke, 2026-09-06: "im nächsten Schritt machen wir die
     * Namen editierbar") — WITHOUT it, `ChangeTracker.getChangedValues()`
     * for an already-persisted row (rename, not create) reports only `{uid}`
     * — the same silent-no-op bug found and fixed for `IssuePersonalData`'s
     * `personalLabel`/`sortOrder` fields (docs/feature-plans/0026-personal-
     * labels-issue-data/): a rename would dispatch `UpdatePersonalLabelCommand`
     * with no `label` key in the payload at all, and the backend handler's
     * `hasPayload('label')` guard would silently skip the update.
     */
    @Public()
    @Expose()
    label: string = ``;

    /**
     * Free-grid position/size (My Organizer "Boxen" grid, 2026-09-13) —
     * gridstack grid-cell units, not pixels, same field shape as `LinkTile`
     * uses for the Linkview grid. `@Public()` for the same reason as `label`
     * above: without it, `ChangeTracker.getChangedValues()` on an
     * already-persisted row would omit these on a drag/resize, and
     * `UpdatePersonalLabelCommand` would silently do nothing (the backend
     * handler's per-field `payloadHasKey()` guards would never see the new
     * values). `null` (the default, and a legal value at any time) means "no
     * position saved yet" — the view renders a default side-by-side layout
     * for those, not this entity's job to decide.
     */
    @Public()
    @Expose()
    x: number | null = null;

    @Public()
    @Expose()
    y: number | null = null;

    @Public()
    @Expose()
    w: number | null = null;

    @Public()
    @Expose()
    h: number | null = null;

    @Expose()
    @Transform(({value}) => !value ? null : DateTime.fromISO(value), {toClassOnly: true})
    @Transform(({value}: { value: DateTime | null }) => value?.toISO() ?? null, {toPlainOnly: true})
    deletedAt: DateTime | null = null;

    constructor() {
        super();

        return ChangeTracker.create(this as PersonalLabel);
    }

    public toString(): string {
        return this.label;
    }
}
